import React from 'react';
import styled from 'styled-components';
import gql from 'graphql-tag';
import { useQuery, useMutation } from '@apollo/react-hooks';
import CircularProgress from '@material-ui/core/CircularProgress';
import Grid from '@material-ui/core/Grid';
import Snackbar from '@material-ui/core/Snackbar';
import Button from '@material-ui/core/Button';
import { useHistory } from 'react-router-dom';

import Block from 'components/common/block';
import EditProject from 'components/common/editProject';
import GeneralInformationField from 'components/common/generalInformationField';
import useEquipmentStatus from 'helpers/useEquipmentStatus';

const GetStsBoxQuery = gql`
  query GetStsBox($id: ID!) {
    node(id: $id) {
      ... on StsBox {
        id
        plNumber
        location
        contents
        status
      }
    }
  }
`;

const UpdateStsBoxMutation = gql`
  mutation UpdateStsBox($input: UpdateStsBoxInput!) {
    updateStsBox(updateStsBoxData: $input) {
      stsbox {
        id
      }
    }
  }
`;

const ArchiveStsBoxMutation = gql`
  mutation ArchiveStsBox($input: ArchiveStsBoxInput!) {
    archiveStsBox(archiveStsBoxData: $input) {
      success
    }
  }
`;

function GeneralInformation({ id, isAdmin }) {
  const [editing, setEditing] = React.useState(false);
  const [plNumber, setPlNumber] = React.useState('');
  const [location, setLocation] = React.useState('');
  const [status, setStatus] = React.useState('');
  const [contents, setContents] = React.useState(['']);
  const [showArchiveSnackbar, setShowArchiveSnackbar] = React.useState(false);

  const history = useHistory();

  const {statusValues} = useEquipmentStatus();

  const { data, error, loading } = useQuery(GetStsBoxQuery, {
    variables: {
      id,
    },
    onCompleted(result) {
      const stsBox = result.node;
      if (!stsBox) return;

      if (stsBox.plNumber) setPlNumber(stsBox.plNumber);
      if (stsBox.location) setLocation(stsBox.location);
      if (stsBox.status) setStatus(stsBox.status);
      if (stsBox.contents) setContents(stsBox.contents);
    },
    onError(e) {
      console.log(e);
    },
    skip: !id,
    fetchPolicy: 'network-only',
  });

  const [executeUpdateMutation, { data: updateData }] = useMutation(UpdateStsBoxMutation, {
    variables: {
      input: {
        stsboxId: id,
        plNumber,
        location,
        status,
        contents,
      },
    },
  });

  const [executeArchive, { loading: archiveLoading }] = useMutation(ArchiveStsBoxMutation, {
    variables: {
      input: {
        stsboxId: id,
      },
    },
    onCompleted: () => {
      history.push('/dashboard/equipment/stsBoxes');
    },
    onError: (e) => {
      console.log(e)
    }
  });


  function save() {
    executeUpdateMutation();
    setEditing(false);
  }

  function cancel() {
    const lastSaved = updateData ? updateData.updateStsBox.stsBox : data.node;

    setPlNumber(lastSaved.plNumber)
    setLocation(lastSaved.location)
    setStatus(lastSaved.status)
    setContents(lastSaved.contents)
  }

  if (loading) return <CircularProgress size={64} style={{ margin: '32px auto', display: 'block' }} />;
  if (error || !data.node) return null;

  const fields = [
    {id: 'plNumber', label: 'PL Nr.', onChange: setPlNumber, value: plNumber, type: 'text'},
    {id: 'location', label: 'Location', onChange: setLocation, value: location, type: 'text'}, 
    {id: 'status', label: 'Status', onChange: setStatus, value: status, type: 'select', options: statusValues()},
    {id: 'contents', label: 'Contents', onChange: val => setContents([val]), value: contents, type: 'contents'}, 
  ];

  const disabled = !plNumber.length || loading || archiveLoading;

  return (
    <Block
      title={
        <BlockTitle>
          GENERAL INFORMATION
          {
            isAdmin && (
              <EditProject
                editing={editing}
                setEditing={setEditing}
                save={save}
                cancel={cancel}
                cancelLabel="Cancel"
                saveDisabled={disabled}
                setShowArchiveSnackbar={() => setShowArchiveSnackbar(true)}
              /> 
            )
          }
        </BlockTitle>
      }
      style={{ marginBottom: '16px' }}
    >
      <Grid container spacing={2}>
        {
          fields.map((field) => <GeneralInformationField key={field.id} field={field} editing={editing} />)
        }
      </Grid>
      <Snackbar
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        open={showArchiveSnackbar}
        autoHideDuration={6000}
        onClose={() => setShowArchiveSnackbar(false)}
        message="Delete this sts box?"
        action={
          <React.Fragment>
            <Button color="secondary" size="small" onClick={executeArchive}>
              YES
            </Button>
            <Button color="secondary" size="small" onClick={() => setShowArchiveSnackbar(false)}>
              NO
            </Button>
          </React.Fragment>
        }
      />
    </Block>
  )
}

const BlockTitle = styled.div`
  display: flex;
  justify-content: space-between;
`;

export default GeneralInformation;
